import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gameReviewService, imageService } from '../services';
import PopularReviews from './PopularReviews';
import Header from './Header';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const GetGame = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobile = useIsMobile();
  const [showAllPros, setShowAllPros] = useState(false);
  const [showAllCons, setShowAllCons] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);
  const [pendingDownloadLink, setPendingDownloadLink] = useState(null);
  const [adLoading, setAdLoading] = useState(false);
  const [modalStep, setModalStep] = useState('ad'); // 'ad' | 'ready'
  const [modalType, setModalType] = useState('download'); // 'premium' | 'download'

  // Load Google Analytics script for Fortnite only
  useEffect(() => {
    let gtagScript = null;
    
    if (slug === 'fortnite') {
      // Load gtag script only once if not already loaded
      if (!window.gtag) {
        gtagScript = document.createElement('script');
        gtagScript.async = true;
        gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17208874212';
        document.head.appendChild(gtagScript);

        // Initialize gtag
        window.dataLayer = window.dataLayer || [];
        function gtag(){window.dataLayer.push(arguments);}
        window.gtag = gtag;
        
        gtagScript.onload = () => {
          // eslint-disable-next-line no-undef
          gtag('js', new Date());
          // eslint-disable-next-line no-undef
          gtag('config', 'AW-17208874212');
        };
      } else {
        // If gtag already exists, just track the page view
        // eslint-disable-next-line no-undef
        gtag('config', 'AW-17208874212', {
          page_title: 'Get Game - Fortnite',
          page_location: window.location.href
        });
      }
    } else {
      // If not Fortnite, cleanup any existing gtag script to avoid tracking other games
      const existingScript = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
      // Clear gtag function to prevent tracking
      if (window.gtag) {
        try {
          delete window.gtag;
        } catch (e) {
          // If delete fails, set to undefined instead
          window.gtag = undefined;
        }
      }
    }

    // Cleanup function when component unmounts
    return () => {
      if (gtagScript && gtagScript.parentNode) {
        gtagScript.parentNode.removeChild(gtagScript);
      }
    };
  }, [slug]);

  useEffect(() => {
    setLoading(true);
    const fetchGameData = async () => {
      try {
        const res = await gameReviewService.getBySlug(slug);
        setGameData(res?.data || null);
        if (res?.data?.title) {
          document.title = res.data.title;
        }
      } catch (err) {
        setError('Không tìm thấy thông tin game!');
      } finally {
        setLoading(false);
      }
    };
    fetchGameData();
  }, [slug]);

  useEffect(() => {
    if (!loading) {
      setShowAllPros(false);
      setShowAllCons(false);
    }
  }, [gameData, loading]);

  // Hàm xử lý khi click nút download hoặc premium
  const handleDownloadClick = (link, type = 'download') => {
    // Track conversion event for Fortnite premium button
    if (slug === 'fortnite' && type === 'premium' && window.gtag) {
      // eslint-disable-next-line no-undef
      gtag('event', 'conversion', {
        'send_to': 'AW-17208874212/Xah2CMKMzNwaEOSp6o1A',
        'transaction_id': ''
      });
    }
    
    setPendingDownloadLink(link);
    setShowAdModal(true);
    setModalStep('ad');
    setModalType(type);
  };

  // Hàm chạy quảng cáo và mở link
  const handleAcceptAd = () => {
    setAdLoading(true);
    // Tạo script quảng cáo
    const netlink_script = document.createElement('script');
    netlink_script.src = 'https://cdn.jsdelivr.net/gh/adsbyNetlink/nl@master/adsNetlink.js';
    document.head.appendChild(netlink_script);
    netlink_script.onload = function() {
      if (window.NetlinkAdxRewarded) {
        window.NetlinkAdxRewarded('/93656639/gamelim.com/gamelim.com_reward_getgame', () => {
          // Callback sau khi xem xong quảng cáo
          setAdLoading(false);
        });
      } else {
        // Nếu không có NetlinkAdxRewarded thì mở luôn
        setAdLoading(false);
      }
    };
    // Luôn chuyển sang bước ready sau 5 giây kể từ khi Accept
    setTimeout(() => {
      setModalStep('ready');
      setAdLoading(false);
    }, 5000);
  };

  // Khi người dùng chọn Continue ở bước ready
  const handleContinue = () => {
    if (modalType === 'premium') {
      navigate('/premium');
    }
    if (pendingDownloadLink) {
      window.open(pendingDownloadLink, '_blank');
    }
    setShowAdModal(false);
    setPendingDownloadLink(null);
    setModalStep('ad');
  };

  // Khi người dùng chọn Cancel ở bước ready
  const handleCancel = () => {
    setShowAdModal(false);
    setPendingDownloadLink(null);
    setModalStep('ad');
  };

  if (loading) return <div style={{ textAlign: 'left' }}>Đang tải...</div>;
  if (error) return <div style={{ textAlign: 'left' }}>{error}</div>;
  if (!gameData) return <div style={{ textAlign: 'left' }}>Không có dữ liệu game.</div>;

  return (
    <>
      <Header />
      <div className="getgame-main" style={{ maxWidth: 1200, margin: '0 auto', padding: '40px 20px', textAlign: 'left' }}>
        <div className="getgame-flex" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 40, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          {/* Main Content */}
          <div className="getgame-content" style={{ flex: 1, maxWidth: 800 }}>
            <div className="getgame-main-title-row" style={{ display: 'flex', flexDirection: 'row', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 24 }}>
              {gameData.thumbnailPath && (
                <img
                  src={imageService.getThumbnail(gameData.thumbnailPath)}
                  alt={gameData.title}
                  className="getgame-img"
                  style={{ width: 96, height: 96, borderRadius: 14, objectFit: 'cover', marginRight: isMobile ? 10 : 28, marginBottom: 0 }}
                />
              )}
              <div>
                <h1 className="getgame-title" style={{ fontSize: 32, fontWeight: 700, margin: 0, color: '#111', textAlign: 'left', letterSpacing: 0.5 }}>{gameData.title}</h1>
                <div className="getgame-stars-row" style={{ display: 'flex', alignItems: 'center', margin: '2px 0 0 0' }}>
                  {/* Hiển thị sao, có nửa sao nếu cần */}
                  {(() => {
                    const fullStars = Math.floor(gameData.rateAverage);
                    const hasHalfStar = gameData.rateAverage % 1 >= 0.25 && gameData.rateAverage % 1 < 0.75;
                    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
                    return (
                      <>
                        {[...Array(fullStars)].map((_, i) => (
                          <span key={i} style={{ color: '#fbbf24', fontSize: '1.5rem', marginRight: 2 }}>★</span>
                        ))}
                        {hasHalfStar && (
                          <span style={{ color: '#fbbf24', fontSize: '1.5rem', marginRight: 2 }}>
                            <svg width="20" height="20" viewBox="0 0 20 20" style={{ display: 'inline', verticalAlign: 'middle' }}>
                              <defs>
                                <linearGradient id="half">
                                  <stop offset="50%" stopColor="#fbbf24" />
                                  <stop offset="50%" stopColor="#e5e7eb" />
                                </linearGradient>
                              </defs>
                              <polygon points="10,1 12.59,6.99 19,7.64 14,12.26 15.18,18.51 10,15.27 4.82,18.51 6,12.26 1,7.64 7.41,6.99" fill="url(#half)" />
                            </svg>
                          </span>
                        )}
                        {[...Array(emptyStars)].map((_, i) => (
                          <span key={i} style={{ color: '#e5e7eb', fontSize: '1.5rem', marginRight: 2 }}>★</span>
                        ))}
                      </>
                    );
                  })()}
                </div>
                <div className="getgame-trademark" style={{ color: '#bdbdbd', fontSize: isMobile ? 13 : 15, margin: '2px 0 0 0' }}>
                  All trademarks belong to their respective owners.
                </div>
              </div>
            </div>

            {/* Premium Access Button */}
            <button
              className="getgame-btn"
              style={{
                width: 'fit-content',
                minWidth: 600,
                maxWidth: 800,
                padding: '16px 0',
                background: '#00c3f9',
                color: '#fff',
                fontWeight: 700,
                fontSize: 18,
                border: 'none',
                borderRadius: 32,
                cursor: 'pointer',
                margin: '0 auto 32px auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                boxShadow: '0 4px 16px 0 rgba(0,195,249,0.13)',
                animation: 'premium-pulse 1.2s infinite'
              }}
              onClick={() => {
                handleDownloadClick(undefined, 'premium');
              }}
            >
              {isMobile ? 'Access to premium content (NO FEE)' : 'Get access to the premium content (NO FEE)'}<span className="lock-icon" style={{fontSize: 22, marginLeft: 6}}>🔒</span>
            </button>
            <style>
              {`
                @keyframes premium-pulse {
                  0% { box-shadow: 0 4px 16px 0 rgba(0,195,249,0.13), 0 0 0 0 rgba(0,195,249,0.18); }
                  50% { box-shadow: 0 8px 32px 0 rgba(0,195,249,0.25), 0 0 24px 8px #00c3f9; }
                  100% { box-shadow: 0 4px 16px 0 rgba(0,195,249,0.13), 0 0 0 0 rgba(0,195,249,0.18); }
                }
              `}
            </style>

            {/* Show up to 5 images horizontally if available */}
            {Array.isArray(gameData?.imagePaths) && gameData.imagePaths.length > 0 && (
              <div style={{
                display: 'flex',
                gap: 14,
                margin: '0 auto 24px auto',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}>
                {gameData.imagePaths.slice(0, 5).map((img, idx) => (
                  <img
                    key={idx}
                    src={imageService.getThumbnail(img)}
                    alt={`Screenshot ${idx + 1}`}
                    style={{
                      width: 120,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 8,
                      background: '#eee',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                  />
                ))}
              </div>
            )}

            {/* Rating Section */}
            <div className="getgame-section" style={{ marginBottom: 40 }}>
              <h2 style={{ fontSize: 24, fontWeight: 600, marginBottom: 24 }}>Rating</h2>
              {[
                { label: 'Graphics and Sound', value: gameData.graphicsAndSoundRating },
                { label: 'Controls', value: gameData.controlsRating },
                { label: 'Gameplay', value: gameData.gameplayRating },
                { label: 'Lasting Appeal', value: gameData.lastingAppealRating },
              ].map((item, idx) => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
                  <div className="getgame-rating-label" style={{ width: 180, fontWeight: 600 }}>{item.label}</div>
                  <div style={{ flex: 1, margin: '0 18px' }}>
                    <div style={{
                      width: '100%',
                      height: 12,
                      background: '#e5e7eb',
                      borderRadius: 8,
                      overflow: 'hidden',
                      position: 'relative',
                    }}>
                      <div style={{
                        width: `${(item.value / 5) * 100}%`,
                        height: '100%',
                        background: '#10a6c7',
                        borderRadius: 8,
                        transition: 'width 0.4s',
                      }} />
                    </div>
                  </div>
                  <div className="getgame-rating-value" style={{ width: 32, textAlign: 'right', fontWeight: 700, color: '#10a6c7', fontSize: 18 }}>{item.value}</div>
                </div>
              ))}
            </div>

            {/* Pros and Cons Section */}
            <div style={{ marginBottom: 40 }}>
              <div style={{ display: 'flex', gap: 48, flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontWeight: 700, marginBottom: 12 }}>PROS</h3>
                  <ul style={{ paddingLeft: 18, margin: 0 }}>
                    {(() => {
                      const pros = Array.isArray(gameData.prosAndCons) ? gameData.prosAndCons.filter(i => i.type === 1) : [];
                      const maxShow = isMobile ? 2 : pros.length;
                      const showList = isMobile && !showAllPros ? pros.slice(0, maxShow) : pros;
                      return (
                        <>
                          {showList.map((item, idx) => (
                            <li key={idx} style={{ color: '#10a6c7', marginBottom: 8, fontSize: 16 }}>
                              <span style={{ color: '#222' }}>{item.content}</span>
                            </li>
                          ))}
                          {isMobile && pros.length > 2 && (
                            <li style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              <button onClick={() => setShowAllPros(v => !v)} style={{ background: 'none', border: 'none', color: '#00c3f9', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 15 }}>
                                {showAllPros ? 'Show less' : 'Read more'}
                              </button>
                            </li>
                          )}
                        </>
                      );
                    })()}
                  </ul>
                </div>
                <div style={{ flex: 1, marginTop: isMobile ? 24 : 0 }}>
                  <h3 style={{ fontWeight: 700, marginBottom: 12 }}>CONS</h3>
                  <ul style={{ paddingLeft: 18, margin: 0 }}>
                    {(() => {
                      const cons = Array.isArray(gameData.prosAndCons) ? gameData.prosAndCons.filter(i => i.type === 2) : [];
                      const maxShow = isMobile ? 2 : cons.length;
                      const showList = isMobile && !showAllCons ? cons.slice(0, maxShow) : cons;
                      return (
                        <>
                          {showList.map((item, idx) => (
                            <li key={idx} style={{ color: '#10a6c7', marginBottom: 8, fontSize: 16 }}>
                              <span style={{ color: '#222' }}>{item.content}</span>
                            </li>
                          ))}
                          {isMobile && cons.length > 2 && (
                            <li style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                              <button onClick={() => setShowAllCons(v => !v)} style={{ background: 'none', border: 'none', color: '#00c3f9', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 15 }}>
                                {showAllCons ? 'Show less' : 'Read more'}
                              </button>
                            </li>
                          )}
                        </>
                      );
                    })()}
                  </ul>
                </div>
              </div>
            </div>

            {/* Download Section */}
            <div className="getgame-section" style={{ 
              background: '#fff', 
              borderRadius: 12, 
              padding: 24, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: 24 
            }}>
              <h2 style={{ fontSize: 24, fontWeight: 600, marginBottom: 16 }}>Get from the official digital markets</h2>
              <div style={{ color: '#bdbdbd', fontSize: 15, marginBottom: 24 }}>
                All trademarks belong to their respective owners.
              </div>
              <div style={{ display: 'flex', gap: 16 }}>
                {gameData.downloadLinkOriginalSite && (
                  <button
                    onClick={() => handleDownloadClick(gameData.downloadLinkOriginalSite, 'download')}
                    style={{
                      flex: 1,
                      padding: '14px 32px',
                      background: '#00c3f9',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 16,
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 4px 12px rgba(16,166,199,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10
                    }}
                  >
                    Get from the Official site
                  </button>
                )}
                {gameData.downloadLinkAndroid && (
                  <button
                    onClick={() => handleDownloadClick(gameData.downloadLinkAndroid, 'download')}
                    style={{
                      flex: 1,
                      padding: '14px 32px',
                      background: '#00c3f9',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 16,
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 4px 12px rgba(16,166,199,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10
                    }}
                  >
                    Get Android Version
                  </button>
                )}
                {gameData.downloadLinkIos && (
                  <button
                    onClick={() => handleDownloadClick(gameData.downloadLinkIos, 'download')}
                    style={{
                      flex: 1,
                      padding: '14px 32px',
                      background: '#00c3f9',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 16,
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 4px 12px rgba(16,166,199,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10
                    }}
                  >
                    Get iOS Version
                  </button>
                )}
              </div>
            </div>
            {/* Modal xác nhận quảng cáo */}
            {showAdModal && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.35)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <div style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: 32,
                  maxWidth: 400,
                  width: '90%',
                  boxShadow: '0 2px 16px rgba(0,0,0,0.18)',
                  textAlign: 'center',
                }}>
                  {modalStep === 'ad' ? (
                    <>
                      <div style={{ fontWeight: 700, fontSize: 26, marginBottom: 18, color: '#111', lineHeight: 1.2 }}>
                        {modalType === 'premium'
                          ? 'This is a premium content, please watch the short advertisement to enter'
                          : 'Watch a reward ad and go to the download link!'}
                      </div>
                      <div style={{ display: 'flex', gap: 18, justifyContent: 'center', marginTop: 24 }}>
                        <button
                          onClick={() => { setShowAdModal(false); setPendingDownloadLink(null); setModalStep('ad'); setModalType('download'); }}
                          style={{
                            flex: 1,
                            padding: '12px 0',
                            background: '#00c3f9',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 18,
                            border: 'none',
                            borderRadius: 24,
                            cursor: adLoading ? 'not-allowed' : 'pointer',
                            opacity: adLoading ? 0.6 : 1,
                            transition: 'all 0.2s',
                          }}
                          disabled={adLoading}
                        >No</button>
                        <button
                          onClick={handleAcceptAd}
                          style={{
                            flex: 1,
                            padding: '12px 0',
                            background: '#ff6b5b',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 18,
                            border: 'none',
                            borderRadius: 24,
                            cursor: adLoading ? 'not-allowed' : 'pointer',
                            opacity: adLoading ? 0.6 : 1,
                            transition: 'all 0.2s',
                          }}
                          disabled={adLoading}
                        >{adLoading ? 'Loading...' : 'Accept'}</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 18, color: '#111', lineHeight: 1.2 }}>
                        {modalType === 'premium'
                          ? 'The premium content is ready. Would you like to continue?'
                          : 'The download link is ready. Would you like to continue?'}
                      </div>
                      <div style={{ display: 'flex', gap: 18, justifyContent: 'center', marginTop: 24 }}>
                        <button
                          onClick={handleCancel}
                          style={{
                            flex: 1,
                            padding: '12px 0',
                            background: '#bdbdbd',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 18,
                            border: 'none',
                            borderRadius: 24,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                        >Cancel</button>
                        <button
                          onClick={handleContinue}
                          style={{
                            flex: 1,
                            padding: '12px 0',
                            background: '#00c3f9',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: 18,
                            border: 'none',
                            borderRadius: 24,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                        >Continue</button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar chỉ hiện trên desktop */}
          {!isMobile && (
            <div className="getgame-sidebar" style={{ width: 360, flexShrink: 0 }}>
              <div style={{ 
                background: '#fff', 
                borderRadius: 12, 
                padding: 24, 
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                marginBottom: 24 
              }}>
                <PopularReviews />
              </div>
            </div>
          )}
        </div>
        {/* PopularReviews dưới cùng trên mobile */}
        {isMobile && (
          <div className="getgame-sidebar" style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', margin: '24px 0 0 0' }}>
            <PopularReviews />
          </div>
        )}
      </div>
    </>
  );
};

export default GetGame; 